import React, { useCallback, useEffect, useRef, useState } from 'react';
import { gridSpacing } from '../../common/themes/constants';
import { Grid, Card, CardContent, CardHeader, Alert } from '@mui/material';
import { HookFormUtils, MyTypography } from '../common/components';
import { capitalize, debounce, isEmpty } from 'lodash';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useGetPatient } from '../referral/redux/getPatient';
import Swal from 'sweetalert2';
import BackendErrorHandler from '../common/components/hook-form/BackendErrorHandler';
import { useSaveReferralAsDraft } from '../referral/redux/saveReferralAsDraft';
import Error from '../common/Error';
import PatientDemographicsForm from './PatientDemographicsForm';
import { useLazyQuery } from '@apollo/client';
import clientGraphql from '../../common/apollo-graphql';
import { LOAD_PATIENT_BY_SSN } from '../referral/gql/Query';
import moment from 'moment';

export default function PatientDemographics({
  referralUuid,
  onSetReferralId,
  parsedData,
  prefix,
  onDisableNext = null,
  isSavedDraft = false,
  isSameOrg = false,
  patientSchema = {},
  referralSchema = {},
  requiredCustom = [],
  initialValues = {},
  isFax = false,
  lastRequiredIndex = -1,
  providers = [],
  showOnlyPatientDemographics = false
}) {
  const gqlParams = {
    client: clientGraphql,
    variables: {
      ssn: '',
    },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'network-only',
    refetchOnWindowFocus: false,
  };
  const [loadPatientBySsn] = useLazyQuery(LOAD_PATIENT_BY_SSN, gqlParams);
  const {
    control,
    getValues,
    setValue,
    trigger,
    reset,
    setError,
    clearErrors,
    formState,
    watch,
  } = useForm({
    defaultValues: { ...initialValues },
    resolver: yupResolver(
      yup.object().shape({
        referral: yup.object().shape(referralSchema),
        patient: yup.object().shape(patientSchema),
      }),
    ),
    mode: 'all',
    reValidateMode: 'onChange',
  });
  const isFetchingRef = useRef(false);
  const [details, setDetails] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [loadedSSN] = useState({});
  const {
    saveReferralAsDraft,
    saveReferralAsDraftPending,
    saveReferralAsDraftError,
    dismissSaveReferralAsDraftError,
  } = useSaveReferralAsDraft();

  const handleRetrieve = useCallback((res) => {
    setValue('patient.full_address', res.full_address);
    trigger('patient.full_address');
  }, [setValue, trigger]);

  const [referralUpdatedMessage, setReferralUpdatedMessage] = useState('');
  const debouncedSave = useCallback(
    debounce(async (referralUUID, args) => {
      setIsSaving(true);
      dismissSaveReferralAsDraftError();
      await saveReferralAsDraft(referralUUID, args)
        .then(data => {
          const { referral_uuid } = data;
          setIsSaving(false);
          if (isEmpty(referralUUID)) {
            onSetReferralId(referral_uuid);
          }
          reset({}, { keepValues: true, keepIsValid: false });
          setReferralUpdatedMessage(data.message);
        })
        .catch(err => {
          setIsSaving(false);
          onDisableNext(true);
          reset({}, { keepValues: true, keepIsValid: false });
        });
    }, 1500),
    [],
  );

  const hasAddressDirtyField = useCallback(() => {
    return 'patient' in formState.dirtyFields && 'full_address' in formState.dirtyFields.patient;
  }, [formState]);

  const { getPatient, dismissGetPatientError } = useGetPatient();

  const onGetPatient = useCallback(() => {
    dismissGetPatientError();
    return getPatient(referralUuid)
      .then(data => {
        setDetails({
          ...data.data
        });
      })
      .catch(err => {
        Swal.fire({
          icon: 'error',
          text: err?.data,
          showConfirmButton: true,
        });
      });
  }, [getPatient, dismissGetPatientError, referralUuid]);

  const updatedValues = useCallback(() => {
    if (!isEmpty(referralUuid) && !formState.isValidating && !hasAddressDirtyField()) {
      return HookFormUtils.dirtyValues(formState.dirtyFields, getValues());
    } else if (!isEmpty(referralUuid) && !formState.isValidating && hasAddressDirtyField()) {
      return {
        patient: {
          full_address: getValues('patient.full_address'),
        },
      };
    } else if (isEmpty(referralUuid) && !formState.isValidating) {
      return getValues();
    }
  }, [formState, getValues, hasAddressDirtyField, referralUuid]);

  const setReferralData = useCallback((referral) => {
    setValue('referral.diagnosis', referral.diagnosis?.description || '');
    setValue('referral.comment', referral.comment || '');
    setValue('referral.is_fax', referral.is_fax || false);
    setValue('referral.referrer_provider_id', referral.referrer_provider?.id || '');
    if (!isEmpty(referral) && !isEmpty(referral.extra_info)) {
      var extra_info = JSON.parse(referral.extra_info);
      if (!isEmpty(extra_info)) {
        for (const [key, value] of Object.entries(extra_info)) {
          setValue('referral.' + key, value);
        }
      }
    }

    trigger('referral');
  }, [setValue, trigger]);

  const setPatientData = useCallback((patient) => {
    setValue('patient.ssn', patient.ssn);
    setValue('patient.mrn', patient.mrn || '');
    setValue('patient.firstname', patient.firstname);
    setValue('patient.lastname', patient.lastname);
    setValue('patient.birthdate', patient.birthdate);
    setValue('patient.phone', patient.phone);
    setValue('patient.insurance_provider', patient.insurance_provider);
    setValue('patient.policy_number', patient.policy_number);
    setValue('patient.full_address', patient.full_address);

    trigger('patient');
  }, [setValue, trigger]);

  useEffect(() => {
    if (!isEmpty(referralUuid) && !isEmpty(details)) {
      setPatientData(details.patient);
      setReferralData(details.referral);
    }
  }, [setPatientData, setReferralData, details, referralUuid]);

  useEffect(() => {
    if (!isEmpty(parsedData)) {
      setValue('referral.clinic_receiver_id', parsedData.clinic.id);
      setValue('referral.provider_id', parsedData.clinic.provider_id);
      setValue('referral.is_fax', parsedData.clinic.isFax || false);
      trigger('referral.clinic_receiver_id');
      trigger('referral.provider_id');
      trigger('referral.is_fax');
    }
  }, [trigger, setValue, parsedData, reset, getValues]);

  useEffect(() => {
    if (
      referralUuid &&
      isEmpty(getValues('patient.ssn')) &&
      isEmpty(details) &&
      !isFetchingRef.current
    ) {
      isFetchingRef.current = true;
      onGetPatient().finally(() => {
        isFetchingRef.current = false;
      });
    }
  }, [referralUuid, details, getValues, onGetPatient]);


  useEffect(() => {
    if (formState.isValid && !formState.isValidating && !isEmpty(formState.dirtyFields)) {
      const val = JSON.parse(JSON.stringify(updatedValues()));

      if (val?.patient?.ssn) {
        val.patient.ssn = val.patient.ssn.replace(/-/g, '');
      }
      if (val?.patient?.phone) {
        val.patient.phone = val.patient.phone.replace(/-/g, '');
      }
      if (val?.patient?.birthdate) {
        val.patient.birthdate = moment(val.patient.birthdate).format('YYYY-MM-DD');
      }

      if (!isSaving) {
        if (!isEmpty(val) && !isEmpty(val.referral)) {
          val.referral.is_fax = parsedData.clinic.isFax;
        }

        debouncedSave(referralUuid, val)
      }
    }

    onDisableNext(!formState.isValid);
  }, [
    formState,
    isSaving,
    referralUuid,
    debouncedSave,
    onDisableNext,
    parsedData,
    updatedValues,
  ]);

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (
        name === 'patient.ssn' &&
        value.patient.ssn.length === 11 &&
        isEmpty(referralUuid)
      ) {
        loadPatientBySsn({
          client: clientGraphql,
          variables: { ssn: value.patient.ssn.replaceAll('-', '') },
        }).then(result => {
          if (
            result.hasOwnProperty('data') &&
            !isEmpty(result.data) &&
            result.data.hasOwnProperty('patient_by_ssn')
          ) {
            const { patient_by_ssn: patient } = result.data;
            if (!isEmpty(patient)) {
              setPatientData(patient);
            }
          }
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, referralUuid, loadPatientBySsn, loadedSSN, setPatientData, setValue]);

  return (
    <Card>
      {!showOnlyPatientDemographics ?
        <CardHeader
          title={parsedData.clinic.provider_name + (parsedData.specialty ? ' - ' : '') + capitalize(parsedData.specialty.name)}
          subheader={parsedData.clinic.name + ' - ' + parsedData.clinic.address}
        /> : null
      }
      <CardContent>
        <Grid container spacing={gridSpacing}>
          {!showOnlyPatientDemographics ?
            <Grid item sm={12} xs={12}>
              <MyTypography>
                STATUS:{' '}
                {!isEmpty(referralUuid)
                  ? 'Saved as Draft'
                  : saveReferralAsDraftPending
                    ? 'Submitting...'
                    : 'Unsaved'}
              </MyTypography>
            </Grid> : null
          }
          <Grid item sm={12} xs={12}>
            {saveReferralAsDraftError && (
              <Grid item md={12} xs={12} sx={{ pb: 2 }}>
                <BackendErrorHandler
                  error={saveReferralAsDraftError}
                  setError={setError}
                  clearErrors={clearErrors}
                />
                <Error error={saveReferralAsDraftError} />
              </Grid>
            )}

            {showOnlyPatientDemographics && referralUpdatedMessage && (
              <Grid item md={12} xs={12} sx={{ pb: 2 }}>
                <Alert severity="info" sx={{ width: '100%' }}>
                  {referralUpdatedMessage}
                </Alert>
              </Grid>
            )}

            <PatientDemographicsForm
              control={control}
              prefix={prefix}
              errors={formState.errors}
              handleRetrieve={handleRetrieve}
              requiredCustom={requiredCustom}
              isSameOrg={isSameOrg}
              lastRequiredIndex={lastRequiredIndex}
              providers={providers}
              watch={watch}
              setValue={setValue}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
